import React, { useState, useEffect } from 'react';
import './Home.css';

const Home: React.FC = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  const stats = [
    { value: 35, label: 'Active Members', symbol: '+' },
    { value: 85, label: 'Placement Rate', symbol: '%' },
  ];
  
  const testimonials = [
    {
      quote: "Price is what you pay; value is what you get.",
      author: "Warren Buffett",
      position: "Chairman and CEO",
      company: ""
    },
    {
      quote: "Success is not final, failure is not fatal: It is the courage to continue that counts.",
      author: "Winston Churchill",
      position: "Former British Prime Minister",
      company: ""
    },
    {
      quote: "The market can remain irrational longer than you can remain solvent.",
      author: "John Maynard Keynes",
      position: "Economist",
      company: ""
    }
  ];
  

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prevSlide) => (prevSlide + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.querySelector('.stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        const isInViewport = rect.top <= window.innerHeight * 0.75;
        setIsVisible(isInViewport);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="home">
      {/* Enhanced Hero Section with Dynamic Imagery */}
      <div className="hero-section">
        <div className="hero-background">
          <img 
            src="/assets/herobg.jpg" 
            alt="Adelphi University Finance Club" 
            className="hero-bg-main" 
          />
          <img 
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" 
            alt="Finance meeting" 
            className="hero-bg-overlay hero-bg-overlay-1" 
          />
          <img 
            src="https://images.unsplash.com/photo-1642543492481-44e81e3914a6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80" 
            alt="Stock market analysis" 
            className="hero-bg-overlay hero-bg-overlay-2" 
          />
          <div className="hero-accent hero-accent-1"></div>
          <div className="hero-accent hero-accent-2"></div>
        </div>
        <div className="overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Adelphi University Finance Club</h1>
          <div className="title-underline"></div>
          <p className="hero-subtitle">Preparing Future Leaders in Finance at Adelphi University</p>
          <div className="hero-buttons">
            <a href="#about" className="primary-btn">Learn More</a>
            <a href="#market-updates" className="secondary-btn">Market Updates</a>
          </div>
        </div>
      </div>

      <div className="about-section" id="about">
        <div className="container">
          <div className="section-header">
            <h2>About Us</h2>
            <div className="section-underline"></div>
          </div>
          <div className="about-content">
            <div className="about-image">
              <div className="image-container">
                <img src="/assets/team.png" alt="Finance Club members at Adelphi University" />
                <div className="image-overlay"></div>
              </div>
              <div className="image-accent"></div>
            </div>
            <div className="about-text">
              <h3>We Connect, Educate, and Elevate</h3>
              <p>The Adelphi University Finance Club is a premier student organization dedicated to bridging the gap between academic learning and real-world finance. We provide our members with unique opportunities to develop professional skills, network with industry leaders, and gain practical knowledge that gives them a competitive edge in the job market.</p>
              <p>Founded in 2010 at Adelphi's Robert B. Willumstad School of Business, we've grown into one of the most active and influential clubs on campus, with a strong alumni network across major financial institutions.</p>
              <div className="about-features">
                <div className="feature">
                  <div className="feature-icon networking"></div>
                  <h4>Networking</h4>
                  <p>Connect with industry professionals and peers</p>
                </div>
                <div className="feature">
                  <div className="feature-icon education"></div>
                  <h4>Education</h4>
                  <p>Workshops, seminars, and hands-on training</p>
                </div>
                <div className="feature">
                  <div className="feature-icon career"></div>
                  <h4>Career</h4>
                  <p>Internship and job placement assistance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div className="stat-item" key={index}>
                <div className="stat-value">
                  <span className={`counter ${isVisible ? 'animate' : ''}`} data-target={stat.value}>
                    {isVisible ? stat.value : '0'}
                  </span>
                  <span className="stat-symbol">{stat.symbol}</span>
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="market-updates-section" id="market-updates">
        <div className="container">
          <div className="section-header">
            <h2>Q1 2025 Financial Outlook</h2>
            <div className="section-underline"></div>
            <p className="section-subtitle">Comprehensive analysis of market trends, sector performance, and investment opportunities for the first quarter of 2025</p>
            <div className="report-details">
              <p>March 15, 2025 | 24 pages</p>
              <p>Prepared by: AFC Research Team</p>
            </div>
          </div>
          <div className="allocation-buttons">
            <a href="https://drive.google.com/drive/folders/1GeisVtHUoVLxdqCAvVjW0cD4hkFA8tzE?usp=sharing" target="_blank" rel="noopener noreferrer" className="allocation-button">
              Sector Allocation Spring 2025
            </a>
            <a href="https://drive.google.com/drive/folders/1GeisVtHUoVLxdqCAvVjW0cD4hkFA8tzE?usp=sharing" target="_blank" rel="noopener noreferrer" className="allocation-button">
              Asset Allocation Spring 2025
            </a>
          </div>
        </div>
      </div>

      <div className="testimonials-section">
        <div className="container">
          <div className="section-header light">
            <h2>Buisness Wisdom</h2>
            <div className="section-underline"></div>
          </div>
          <div className="testimonials-slider">
            <div className="testimonials-track" style={{ transform: `translateX(-${activeSlide * 100}%)` }}>
              {testimonials.map((testimonial, index) => (
                <div className="testimonial-slide" key={index}>
                  <div className="testimonial-content">
                    <div className="quote-icon"></div>
                    <blockquote>{testimonial.quote}</blockquote>
                    <div className="testimonial-author">
                      <div className="author-name">{testimonial.author}</div>
                      <div className="author-title">{testimonial.position}, {testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="slider-controls">
              {testimonials.map((_, index) => (
                <button 
                  key={index} 
                  className={`slider-dot ${activeSlide === index ? 'active' : ''}`}
                  onClick={() => setActiveSlide(index)}
                  aria-label={`Testimonial ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="leadership-section">
  <div className="container">
    <div className="section-header">
      <h2>Our Leadership Team</h2>
      <div className="section-underline"></div>
    </div>
    <div className="leadership-intro">
      <p>Meet the dedicated student officers who drive our initiatives and ensure the success of the Adelphi University Finance Club.</p>
    </div>
    <div className="leadership-grid">
      
      <div className="leader-card">
        <div className="leader-image">
          <img src="/assets/meeqan2.png" alt="Founder and President" />
          <div className="social-links">
            <a 
              href="https://www.linkedin.com/in/meeqan-aylia-1a89451ba?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BFbt5i3HMTTOrNLuAE9qjpg%3D%3D" 
              className="social-link linkedin" 
              aria-label="LinkedIn" 
              target="_blank" 
              rel="noopener noreferrer">
            </a>
            <a href="#" className="social-link email" aria-label="Email"></a>
          </div>
        </div>
        <div className="leader-info">
          <h3>Meeqan Aylia</h3>
          <div className="leader-title">Founder and President</div>
          <p>Finance Major | Freshman | Hewlett, NY</p>
          <blockquote className="leader-quote">
            “We don’t have to be smarter than the rest. We have to be more disciplined than the rest.” ― Warren Buffett
          </blockquote>
        </div>
      </div>
      
      
      <div className="leader-card">
        <div className="leader-image">
          <img src="/assets/hugh.png" alt="Vice President" />
          <div className="social-links">
            <a 
              href="https://www.linkedin.com/in/hughckeenan?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BYHIqFm%2F%2FReKnfg3Zq8BpKw%3D%3D" 
              className="social-link linkedin" 
              aria-label="LinkedIn" 
              target="_blank" 
              rel="noopener noreferrer">
            </a>
            <a href="#" className="social-link email" aria-label="Email"></a>
          </div>
        </div>
        <div className="leader-info">
          <h3>Hugh Keenan</h3>
          <div className="leader-title">Vice President</div>
          <p>Finance Major | Senior | Garden City, NY</p>
          <blockquote className="leader-quote">"Markets are constantly in a state of uncertainty and flux, and money is made by discounting the obvious and betting on the unexpected."-George Soros</blockquote>
        </div>
      </div>
      
      
      <div className="leader-card">
        <div className="leader-image">
          <img src="/assets/andrew.jpg" alt="Treasurer" />
          <div className="social-links">
            <a 
              href="https://www.linkedin.com/in/andrew-bogorodovsky-7a1b23326?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BqL3E%2FeDURveVxzBvYMJRwA%3D%3D" 
              className="social-link linkedin" 
              aria-label="LinkedIn" 
              target="_blank" 
              rel="noopener noreferrer">
            </a>
            <a href="#" className="social-link email" aria-label="Email"></a>
          </div>
        </div>
        <div className="leader-info">
          <h3>Andrew Bogorodovsky</h3>
          <div className="leader-title">Treasurer</div>
          <p>Finance Major | Sophomore | Queens, NY</p>
          <blockquote className="leader-quote">"Know what you own, and know why you own it." - Peter Lynch</blockquote>
        </div>
      </div>
      
      
      <div className="leader-card">
        <div className="leader-image">
          <img src="/assets/birkin2.jpg" alt="Graduate School Relations Officer" />
          <div className="social-links">
            <a 
              href="https://www.linkedin.com/in/briken-rama-665048231?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BrD5uQJyYScm1GvxHQsvXFQ%3D%3D" 
              className="social-link linkedin" 
              aria-label="LinkedIn" 
              target="_blank" 
              rel="noopener noreferrer">
            </a>
            <a href="#" className="social-link email" aria-label="Email"></a>
          </div>
        </div>
        <div className="leader-info">
          <h3>Briken Rama</h3>
          <div className="leader-title">Graduate School Relations Officer</div>
          <p>BS Finance, MS Business Analytics | First Year Graduate | East Meadow, NY</p>
          <blockquote className="leader-quote">"An investment in knowledge pays the best interest."-Benjamin Franklin</blockquote>
        </div>
      </div>
      
      
      <div className="leader-card">
        <div className="leader-image">
          <img src="/assets/justin.png" alt="Recruitment Chair" />
          <div className="social-links">
            <a 
              href="https://www.linkedin.com/in/justin-iavaroni-2352a9313?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BFnx0lALaRy6oIuy1pliIfw%3D%3D" 
              className="social-link linkedin" 
              aria-label="LinkedIn" 
              target="_blank" 
              rel="noopener noreferrer">
            </a>
            <a href="#" className="social-link email" aria-label="Email"></a>
          </div>
        </div>
        <div className="leader-info">
          <h3>Justin Iavaroni</h3>
          <div className="leader-title">Recruitment Chair</div>
          <p>Economics Major | Senior | Hauppauge, NY</p>
          <blockquote className="leader-quote">
            "Money grows on the tree of persistence."- Japanese proverb
          </blockquote>
        </div>
      </div>
      
      
      <div className="leader-card">
        <div className="leader-image">
          <div className="aibar-crop">
            <img src="/assets/aibar2.jpg" alt="Secretary" />
          </div>
          <div className="social-links">
            <a 
              href="https://www.linkedin.com/in/aibar-murat-1aa02420a?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3BwBXUz1ztQxOpzyv9oofX%2Fw%3D%3D" 
              className="social-link linkedin" 
              aria-label="LinkedIn" 
              target="_blank" 
              rel="noopener noreferrer">
            </a>
            <a href="#" className="social-link email" aria-label="Email"></a>
          </div>
        </div>
        <div className="leader-info">
          <h3>Aibar Murat</h3>
          <div className="leader-title">Secretary</div>
          <p>Finance Major | Junior | Almaty, KZ</p>
          <blockquote className="leader-quote">
            "If you want to win the lottery, you have to make the money to buy a ticket." -Louis Bloom
          </blockquote>
        </div>
      </div>
      
    </div>
  </div>
</div>




      <div className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Join Adelphi University's Finance Club Today</h2>
            <p>Gain access to exclusive events, networking opportunities, and career resources.</p>
            <a href="https://myaulife.adelphi.edu/organization/financeclub/" target="_blank" rel="noopener noreferrer" className="cta-button">Become a Member</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;